export type PagedResult<T> = {
    items: T[];
    totalCount?: number | null; // null = invoked from UI, not from server
};

export const mergePagedResults = <T>(
    currentData: PagedResult<T> | undefined,
    newData: PagedResult<T> & {
        offset?: number;
        pageSize?: number;
    },
    {
        equals,
        sort,
        removeMissing,
        isItemProtected,
    }: {
        equals: (a: T, b: T) => boolean;
        sort?: (a: T, b: T) => number;
        removeMissing?: boolean;
        isItemProtected?: (item: T) => boolean;
    },
) => {
    let items = currentData ? [...currentData.items] : [];
    let addedItemsCount = 0;

    // Add new items or update existing ones
    for (const newItem of newData.items) {
        const existingItemIndex = items.findIndex((item) =>
            equals(item, newItem),
        );
        if (existingItemIndex !== -1) {
            items[existingItemIndex] = newItem;
        } else {
            items.push(newItem);
            addedItemsCount++;
        }
    }

    // Sort items
    if (sort) {
        items.sort(sort);
    }

    // Try to remove deleted sessions, it's not bulletproof, but better than nothing
    if (
        removeMissing &&
        typeof newData.offset === 'number' &&
        typeof newData.pageSize === 'number'
    ) {
        const { offset, pageSize } = newData;
        items = items.filter((item, i) => {
            if (i >= offset && i < offset + pageSize) {
                if (isItemProtected?.(item)) {
                    return true;
                }

                return newData.items.some((newItem) => equals(newItem, item));
            }

            return true;
        });
    }

    return {
        items,
        totalCount:
            newData.totalCount ??
            (typeof currentData?.totalCount === 'number'
                ? currentData.totalCount + addedItemsCount
                : null),
        offset: newData.offset,
        pageSize: newData.pageSize,
    };
};
